// CurrentTimeIndicator.tsx
import React, { useLayoutEffect, useRef, useState } from 'react'
import { differenceInCalendarDays } from 'date-fns'
import { getNowInTz } from '../../utils/timezones'

const blueColorVar = 'var(--colors-blue11)'

interface Props {
  timelineStart: Date
  daysPerWeek: number
  activeTz?: string
}

export const CurrentTimeIndicator: React.FC<Props> = ({
  timelineStart,
  daysPerWeek,
  activeTz,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [topOffset, setTopOffset] = useState(0)
  const [dayIndex, setDayIndex] = useState(0)
  const [shouldShowHighlight, setShouldShowHighlight] = useState(false);

  useLayoutEffect(() => {
    if (!containerRef.current) return

    let hours: number, minutes: number
    if (activeTz) {
      const tzNow = getNowInTz(activeTz)
      hours = tzNow.hours
      minutes = tzNow.minutes
    } else {
      const now = new Date()
      hours = now.getHours()
      minutes = now.getMinutes()
    }

    // 1) figure out vertical offset
    const rowHeight = containerRef.current.clientHeight / 24
    setTopOffset((hours + minutes / 60) * rowHeight)

    // 2) figure out which day column we are in
    // Use the timezone-aware "today" to compute the delta
    const now = new Date()
    const delta = differenceInCalendarDays(now, timelineStart)
    const isWithinView = delta >= 0 && delta < daysPerWeek;
    setShouldShowHighlight(isWithinView)
    setDayIndex(delta)
  }, [timelineStart, daysPerWeek, activeTz])

  // render two lines inside that single week-container
  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      {/* the dotted “background” line */}
      <div
        style={{
          position: 'absolute',
          top: topOffset,
          left: 0,
          right: 0,
          borderTop: '2px dotted #ccc',
          zIndex: 3,
        }}
      />

      {/* the blue “current day” highlight */}
      {shouldShowHighlight &&<div
        style={{
          position: 'absolute',
          top: topOffset,
          left: `${(dayIndex / daysPerWeek) * 100}%`,
          width: `${100 / daysPerWeek}%`,
          height: 2,
          backgroundColor: blueColorVar,
          zIndex: 4,
        }}
      />}
    </div>
  )
}
